import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Globe2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { organizationService } from '../../services/api';
import { useTenant } from '../../contexts/TenantContext';
import { userFacingError } from '../../i18n/userFacingError';
import { isPublicConsultaEnabled, isPublicDenunciaEnabled } from '../../utils/publicPages';

export default function SettingsPublicPagesTab({ headerBar }) {
  const { t } = useTranslation('settings');
  const { tenant, patchTenant } = useTenant();
  const [consulta, setConsulta] = useState(true);
  const [denuncia, setDenuncia] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let active = true;
    organizationService
      .getPublicChannels()
      .then(({ data }) => {
        if (!active) return;
        setConsulta(isPublicConsultaEnabled(data));
        setDenuncia(isPublicDenunciaEnabled(data));
      })
      .catch((e) => {
        if (!active) return;
        setConsulta(isPublicConsultaEnabled(tenant));
        setDenuncia(isPublicDenunciaEnabled(tenant));
        toast.error(userFacingError(e, 'settings:publicPagesLoadError'));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [tenant]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await organizationService.updatePublicChannels({
        public_consulta_enabled: consulta,
        public_denuncia_enabled: denuncia,
      });
      patchTenant?.({
        public_consulta_enabled: isPublicConsultaEnabled(data),
        public_denuncia_enabled: isPublicDenunciaEnabled(data),
      });
      toast.success(t('publicPagesSaved'));
    } catch (e) {
      toast.error(userFacingError(e, 'settings:publicPagesSaveError'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="data-list-card flex items-center justify-center gap-2 p-10 text-gray-600">
        <Loader2 className="h-5 w-5 animate-spin" />
        {t('publicPagesLoading')}
      </div>
    );
  }

  return (
    <div className="data-list-card">
      {headerBar(Globe2, t('publicPagesTitle'), t('publicPagesDescription'))}
      <div className="space-y-4 p-5 sm:p-6">
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 bg-slate-50/70 px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
            checked={consulta}
            onChange={(e) => setConsulta(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-semibold text-gray-900">{t('publicConsultaLabel')}</span>
            <span className="mt-0.5 block text-xs text-gray-500">{t('publicConsultaHint')}</span>
          </span>
        </label>
        <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 bg-slate-50/70 px-4 py-3">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-gray-300 text-primary-700 focus:ring-primary-500"
            checked={denuncia}
            onChange={(e) => setDenuncia(e.target.checked)}
          />
          <span>
            <span className="block text-sm font-semibold text-gray-900">{t('publicDenunciaLabel')}</span>
            <span className="mt-0.5 block text-xs text-gray-500">{t('publicDenunciaHint')}</span>
          </span>
        </label>
        <p className="text-xs text-gray-500">{t('publicPagesNote')}</p>
        <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
          {saving ? t('publicPagesSaving') : t('publicPagesSave')}
        </button>
      </div>
    </div>
  );
}
